/**
 * FERP Portal - Component Explorer Page
 *
 * Surfaces the ~80+ components from @ferp/workflow-forms that were previously
 * only accessible through forms-host's SimplifiedApp.tsx monolith.
 *
 * Architecture: Architect Layer (Layer 3) tool for browsing, testing, and
 * deciding which components to promote to first-class portal pages.
 *
 * Two views:
 * - **Features** (default): Routable pages as a feature catalog with layer
 *   assignment toggles — promote features to Admin or User views.
 * - **Components**: Existing component browser from @ferp/workflow-forms.
 */

import React, { useState, useMemo, Suspense } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  NavLink,
  Paper,
  ScrollArea,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Alert,
  Center,
} from '@mantine/core';
import {
  IconSearch,
  IconAlertTriangle,
  IconComponents,
  IconShield,
  IconNetwork,
  IconChartBar,
  IconSettings,
  IconTool,
  IconRobot,
  IconGavel,
  IconActivity,
  IconDatabase,
  IconUpload,
  IconEdit,
  IconKey,
  IconHeart,
  IconBrandStackshare,
  IconEye,
  IconLayoutGrid,
  IconRotate,
} from '@tabler/icons-react';
import { useFeatureCatalog, type CatalogFeature } from '@/hooks/useFeatureCatalog';
import { getIcon } from '@/utils/iconMap';
import type { ViewLayer } from '@/hooks/useTenantSubgraphConfig';

// ============================================================================
// Component registry: lazy-loaded from @ferp/workflow-forms
// Each entry mirrors what forms-host SimplifiedApp.tsx had
// ============================================================================

interface ExplorerComponent {
  id: string;
  label: string;
  category: string;
  description: string;
  component: React.LazyExoticComponent<React.ComponentType<unknown>> | React.ComponentType<unknown>;
  props?: Record<string, unknown>;
}

// Lazy-load all components to keep initial bundle small
const lazy = (factory: () => Promise<{ default: React.ComponentType<unknown> }>) =>
  React.lazy(factory);

// Helper for named exports
const lazyNamed = <T extends Record<string, unknown>>(
  factory: () => Promise<T>,
  name: keyof T,
) =>
  React.lazy(async () => {
    const mod = await factory();
    return { default: mod[name] as React.ComponentType<unknown> };
  });

/**
 * Full component registry organized by category.
 * Mirrors the ~80 views from forms-host SimplifiedApp.tsx.
 */
const COMPONENTS: ExplorerComponent[] = [
  // ── Workflow Design ─────────────────────────────────────────────
  {
    id: 'workflow-system-integrator',
    label: 'Integrated System',
    category: 'Workflow Design',
    description: 'Complete workflow system with spiral visualization and component browser',
    component: lazyNamed(() => import('@ferp/workflow-forms'), 'WorkflowSystemIntegrator'),
  },
  {
    id: 'split-screen-editor',
    label: '4-Wall Editor',
    category: 'Workflow Design',
    description: 'Split-screen 4-wall workflow editor with ecosystem intelligence',
    component: lazyNamed(() => import('@ferp/workflow-forms'), 'SplitScreenWorkflowEditor'),
  },
  {
    id: 'workflow-canvas-editor',
    label: 'Canvas Editor',
    category: 'Workflow Design',
    description: 'Compact 2D canvas with workflow icons, DAG connections, interactive design',
    component: lazyNamed(() => import('@ferp/workflow-forms'), 'WorkflowCanvasEditor'),
  },
  {
    id: 'unified-workflow-designer',
    label: 'Unified Designer',
    category: 'Workflow Design',
    description: 'Unified interface: 4-wall perimeter icons + center canvas + ecosystem command center',
    component: lazyNamed(() => import('@ferp/workflow-forms'), 'UnifiedWorkflowDesigner'),
  },
  {
    id: 'hybrid-workflow-designer',
    label: 'Hybrid Designer (DAG)',
    category: 'Workflow Design',
    description: 'Linear-to-DAG workflow transformation - drag linear workflows to become DAG nodes',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/hybrid-designer'),
      'HybridWorkflowDesigner',
    ),
    props: { workflowId: 'demo-workflow', availableWorkflows: [] },
  },
  {
    id: 'process-visual-editor',
    label: 'Process Visual Editor',
    category: 'Workflow Design',
    description: 'Drag-and-drop workflow designer with real-time validation',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced'),
      'ProcessVisualEditor',
    ),
  },

  // ── Chaos / Ingestion ───────────────────────────────────────────
  {
    id: 'chaos-document-upload',
    label: 'Chaos Document Upload',
    category: 'Chaos Ingestion',
    description: 'Upload source documents (PDF, email, voice) to generate workflow templates via AI',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/chaos/ChaosDocumentUploader'),
      'ChaosDocumentUploader',
    ),
  },
  {
    id: 'conversion-progress',
    label: 'Conversion Progress Tracker',
    category: 'Chaos Ingestion',
    description: 'Real-time 11-phase conversion progress with multi-LLM consensus',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/chaos/ConversionProgressTracker'),
      'ConversionProgressTracker',
    ),
    props: { documentId: 'demo-doc' },
  },
  {
    id: 'template-refinement',
    label: 'Template Refinement Editor',
    category: 'Chaos Ingestion',
    description: 'Review and edit low-confidence fields with evidence attribution',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/chaos/TemplateRefinementEditor'),
      'TemplateRefinementEditor',
    ),
    props: { templateId: 'demo-template' },
  },
  {
    id: 'chaos-workflow-complete',
    label: 'Chaos-to-DSL Complete Workflow',
    category: 'Chaos Ingestion',
    description: 'Upload -> AI conversion (11-phase) -> Human approval -> Template publication',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/sprint7/ChaosTemplateDashboard'),
      'ChaosTemplateDashboard',
    ),
    props: { defaultTab: 'upload' },
  },

  // ── Safety & Compliance ─────────────────────────────────────────
  {
    id: 'safety-command-center',
    label: 'Safety Command Center',
    category: 'Safety & Compliance',
    description: 'Real-time safety monitoring with emergency response and break-glass access',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced/SafetyComplianceCommandCenter'),
      'SafetyComplianceCommandCenter',
    ),
    props: { tenant: 'demo-tenant', domain: 'healthcare' },
  },
  {
    id: 'safety-events',
    label: 'Safety Events Dashboard',
    category: 'Safety & Compliance',
    description: 'Real-time safety event monitoring with GraphQL subscriptions',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/safety/SafetyEventsDashboard'),
      'SafetyEventsDashboard',
    ),
    props: { tenantId: 'demo-tenant' },
  },
  {
    id: 'executive-safety',
    label: 'Executive Safety Dashboard',
    category: 'Safety & Compliance',
    description: 'Board-level safety reporting - multi-domain assessment, risk heat maps',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/safety'),
      'ExecutiveSafetyDashboard',
    ),
  },
  {
    id: 'clinical-safety',
    label: 'Clinical Safety Monitor',
    category: 'Safety & Compliance',
    description: 'Healthcare-specific safety with HIPAA - patient safety, medication interactions',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/safety'),
      'AdvancedClinicalSafetyMonitor',
    ),
  },
  {
    id: 'generic-safety',
    label: 'Generic Safety Monitor',
    category: 'Safety & Compliance',
    description: 'Domain-agnostic safety validation, threshold monitoring, alert management',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/safety'),
      'AdvancedGenericSafetyMonitor',
    ),
    props: { domain: 'generic', tenantId: 'demo-tenant' },
  },
  {
    id: 'realtime-safety',
    label: 'Real-Time Safety Monitor',
    category: 'Safety & Compliance',
    description: 'Live safety metrics, instant alerting, emergency response tracking',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/safety'),
      'RealTimeSafetyMonitor',
    ),
    props: { domain: 'healthcare', tenantId: 'demo-tenant' },
  },
  {
    id: 'compliance-score',
    label: 'Compliance Score Card',
    category: 'Safety & Compliance',
    description: 'AI-powered compliance scoring with auto-fix suggestions (FHIR, X12, ISO 20022)',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/compliance'),
      'ComplianceScoreCard',
    ),
    props: { recommendations: null },
  },
  {
    id: 'compliance-config',
    label: 'Compliance Configuration',
    category: 'Safety & Compliance',
    description: 'Regulatory framework setup, audit requirements, evidence tracking',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/compliance'),
      'ComplianceConfigurationDashboard',
    ),
  },
  {
    id: 'emergency-override',
    label: 'Emergency Override Panel',
    category: 'Safety & Compliance',
    description: 'Break-glass access, emergency controls, audit trail for critical overrides',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/compliance'),
      'EmergencyOverridePanel',
    ),
  },

  // ── SAGA Operations ─────────────────────────────────────────────
  {
    id: 'saga-monitoring',
    label: 'SAGA Monitoring Dashboard',
    category: 'SAGA & Transactions',
    description: 'Life-critical SAGA coordination - distributed transaction tracking',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/monitoring'),
      'SagaMonitoringDashboard',
    ),
  },
  {
    id: 'saga-coordination-health',
    label: 'SAGA Coordination Health',
    category: 'SAGA & Transactions',
    description: 'Compensation tracking, coordinator performance, isolation level enforcement',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/monitoring'),
      'SagaCoordinationHealthMonitor',
    ),
  },
  {
    id: 'saga-audit-trail',
    label: 'SAGA Audit Trail',
    category: 'SAGA & Transactions',
    description: 'Complete transaction history, compensation logs, rollback tracking',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/monitoring'),
      'SagaAuditTrailViewer',
    ),
  },
  {
    id: 'saga-metrics',
    label: 'SAGA Metrics',
    category: 'SAGA & Transactions',
    description: 'Performance analytics, transaction success rates, compensation patterns',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/monitoring'),
      'SagaPrimitiveMetricsDashboard',
    ),
  },
  {
    id: 'saga-execution',
    label: 'SAGA Execution Monitor',
    category: 'SAGA & Transactions',
    description: 'Real-time transaction execution, step-by-step tracking, error detection',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/saga'),
      'SagaExecutionMonitor',
    ),
  },

  // ── Governance & Policy ─────────────────────────────────────────
  {
    id: 'governance-context',
    label: 'Governance Context Manager',
    category: 'Governance & Policy',
    description: 'Enterprise governance control center',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/governance'),
      'GovernanceContextManager',
    ),
  },
  {
    id: 'policy-rule-builder',
    label: 'Policy Rule Builder',
    category: 'Governance & Policy',
    description: 'Zen Engine visual policy builder with dry-run testing',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/governance'),
      'PolicyRuleBuilder',
    ),
  },
  {
    id: 'policy-analytics',
    label: 'Policy Analytics',
    category: 'Governance & Policy',
    description: 'Policy effectiveness, usage patterns, compliance metrics',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/governance/components'),
      'PolicyAnalyticsDashboard',
    ),
  },
  {
    id: 'governance-policy-editor',
    label: 'Governance Policy Editor',
    category: 'Governance & Policy',
    description: 'Integration policies, data governance rules, access controls',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/integration-governance'),
      'GovernancePolicyEditor',
    ),
  },
  {
    id: 'context-inheritance',
    label: 'Context Inheritance Viewer',
    category: 'Governance & Policy',
    description: 'Hierarchical context propagation, tenant/domain/workflow relationships',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/governance/components'),
      'ContextInheritanceViewer',
    ),
  },
  {
    id: 'governance-dashboard',
    label: 'Governance Dashboard',
    category: 'Governance & Policy',
    description: 'Workflow governance: policies, escalations, schedules, resources, integrations',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/sprint7/GovernanceDashboard'),
      'GovernanceDashboard',
    ),
  },
  {
    id: 'regulatory-governance',
    label: 'Regulatory Change Management',
    category: 'Governance & Policy',
    description: 'Complete regulatory change management with point-in-time compliance',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced/RegulatoryChangeManagementSystem'),
      'RegulatoryChangeManagementSystem',
    ),
  },
  {
    id: 'regulatory-interface',
    label: 'Multi-Domain Regulatory Interface',
    category: 'Governance & Policy',
    description: 'Multi-jurisdictional compliance automation with regulatory intersection resolution',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced/MultiDomainRegulatoryInterface'),
      'MultiDomainRegulatoryInterface',
    ),
    props: { tenant: 'demo-tenant', domain: 'healthcare' },
  },

  // ── Analytics & Monitoring ──────────────────────────────────────
  {
    id: 'process-analytics',
    label: 'Process Analytics',
    category: 'Analytics & Monitoring',
    description: 'Bottleneck detection and performance optimization insights',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/analytics'),
      'ProcessAnalyticsDashboard',
    ),
    props: {
      workflowId: 'sample',
      workflowName: 'Sample Workflow',
      timeRange: { start: new Date(Date.now() - 86400000), end: new Date() },
    },
  },
  {
    id: 'c-level-audit',
    label: 'C-Level Audit Intelligence',
    category: 'Analytics & Monitoring',
    description: 'Executive-level compliance metrics and risk scoring',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/executive'),
      'CLevelAuditDashboard',
    ),
    props: {
      timeRange: { start: new Date(Date.now() - 30 * 86400000), end: new Date() },
    },
  },
  {
    id: 'task-analytics',
    label: 'Task Analytics',
    category: 'Analytics & Monitoring',
    description: 'Complete task monitoring with real-time metrics and assignee performance',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/data'),
      'TaskAnalyticsDashboard',
    ),
  },
  {
    id: 'general-monitoring',
    label: 'General Monitoring',
    category: 'Analytics & Monitoring',
    description: 'System health, resource usage, performance tracking',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/monitoring'),
      'MonitoringDashboard',
    ),
  },
  {
    id: 'production-health',
    label: 'Production System Health',
    category: 'Analytics & Monitoring',
    description: 'Production metrics, circuit breakers, and real-time monitoring',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced/ProductionSystemHealthDashboard'),
      'default',
    ),
  },
  {
    id: 'production-monitoring',
    label: 'Production Monitor',
    category: 'Analytics & Monitoring',
    description: 'Live system metrics, error tracking, performance baselines',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced'),
      'ProductionMonitoringDashboard',
    ),
  },
  {
    id: 'system-health',
    label: 'System Health Dashboard',
    category: 'Analytics & Monitoring',
    description: 'Infrastructure monitoring, service health, dependency tracking',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced'),
      'SystemHealthDashboard',
    ),
  },

  // ── Runtime Operations ──────────────────────────────────────────
  {
    id: 'optimization-dashboard',
    label: 'AI Optimization',
    category: 'Runtime Operations',
    description: 'AI-powered workflow optimization recommendations',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/optimization'),
      'OptimizationDashboard',
    ),
  },
  {
    id: 'job-monitoring',
    label: 'Job Monitoring',
    category: 'Runtime Operations',
    description: 'Real-time background job monitoring',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/execution'),
      'JobMonitoringDashboard',
    ),
  },
  {
    id: 'actor-runtime',
    label: 'Actor Runtime Dashboard',
    category: 'Runtime Operations',
    description: 'Actor-based runtime monitoring',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/actors'),
      'ActorRuntimeDashboard',
    ),
  },
  {
    id: 'execution-engines',
    label: 'Execution Engine Selector',
    category: 'Runtime Operations',
    description: 'Configure 4 runtime engines (StateMachine/Saga/Actor/DAG)',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/execution'),
      'ExecutionEngineSelector',
    ),
  },
  {
    id: 'draft-manager',
    label: 'Draft Manager',
    category: 'Runtime Operations',
    description: 'Workflow draft management with auto-save',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/drafts'),
      'DraftManager',
    ),
    props: { kind: 'WorkflowDefinition', userId: 'demo-user' },
  },
  {
    id: 'cache-performance',
    label: 'Cache Performance',
    category: 'Runtime Operations',
    description: 'Valkey cache monitoring and optimization',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/cache'),
      'CachePerformanceDashboard',
    ),
  },
  {
    id: 'failed-job-retry',
    label: 'Failed Job Retry',
    category: 'Runtime Operations',
    description: 'Retry management, error analysis, batch retry operations',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/execution/components'),
      'FailedJobRetryPanel',
    ),
  },
  {
    id: 'instance-tracker',
    label: 'Instance Tracker',
    category: 'Runtime Operations',
    description: 'Real-time workflow instance monitoring with live status updates and timeline',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/instance/InstanceTrackerView'),
      'InstanceTrackerView',
    ),
  },

  // ── Expert Routing ──────────────────────────────────────────────
  {
    id: 'expert-routing',
    label: 'Expert Routing Dashboard',
    category: 'Expert Routing',
    description: 'Intelligent expert assignment, capacity management, skill-based routing',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/expert-routing'),
      'ExpertRoutingDashboard',
    ),
  },
  {
    id: 'routing-metrics',
    label: 'Routing Metrics',
    category: 'Expert Routing',
    description: 'Expert performance, response times, assignment analytics',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/expert-routing'),
      'RoutingMetricsPanel',
    ),
    props: {
      metrics: { totalRoutings: 0, avgResponseTime: 0, expertUtilization: 0, availableExperts: 0, shouldHandoffToSpecialists: false },
      isLoading: false,
    },
  },

  // ── Audit & Forensics ──────────────────────────────────────────
  {
    id: 'forensic-audit',
    label: 'Forensic Audit Viewer',
    category: 'Audit & Forensics',
    description: 'Forensic investigation with tamper detection (SOX 404)',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/audit/BasicForensicAuditViewer'),
      'BasicForensicAuditViewer',
    ),
  },
  {
    id: 'audit-trail-viewer',
    label: 'Audit Trail Viewer',
    category: 'Audit & Forensics',
    description: 'Immutable event logs, compliance evidence, regulatory reporting',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/integration-governance'),
      'AuditTrailViewer',
    ),
  },
  {
    id: 'transaction-monitor',
    label: 'Transaction Monitor',
    category: 'Audit & Forensics',
    description: 'Distributed transaction tracking, multi-system coordination',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/integration-governance'),
      'TransactionMonitor',
    ),
  },

  // ── Primitive Dashboards ────────────────────────────────────────
  {
    id: 'primitive-composition',
    label: 'Primitive Composition Dashboard',
    category: 'Primitive Dashboards',
    description: 'Interactive 25-primitive composition dashboard with real-time loading',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced/PrimitiveCompositionDashboard'),
      'PrimitiveCompositionDashboard',
    ),
    props: { domain: 'assisted_living_facility', tenant: 'demo-tenant' },
  },
  {
    id: 'universal-primitive-registry',
    label: 'Universal Primitive Registry',
    category: 'Primitive Dashboards',
    description: 'Interactive visualization of all FERP workflow primitives with safety integration',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced'),
      'UniversalPrimitiveRegistryDashboard',
    ),
    props: { tenant: 'demo-tenant' },
  },
  {
    id: 'comprehensive-dashboard',
    label: 'Comprehensive Dashboard',
    category: 'Primitive Dashboards',
    description: 'Complete overview of all workflow types',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced'),
      'ComprehensiveDashboard',
    ),
  },
  {
    id: 'lifecycle-plane',
    label: 'Lifecycle Overview',
    category: 'Primitive Dashboards',
    description: 'Visual workflow lifecycle management',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced'),
      'WorkflowLifecyclePlane',
    ),
  },
  {
    id: 'lifecycle-swimlanes',
    label: 'Lifecycle Swimlanes',
    category: 'Primitive Dashboards',
    description: 'Tabulator-powered lifecycle swimlane dashboard',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced'),
      'TabulatorLifecycleDashboard',
    ),
  },
  {
    id: 'workflow-components',
    label: 'Workflow Components Table',
    category: 'Primitive Dashboards',
    description: 'Search and manage workflow components',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced'),
      'WorkflowComponentsTable',
    ),
  },

  // ── Governance Operations ───────────────────────────────────────
  {
    id: 'rbac-management',
    label: 'Role Management (RBAC)',
    category: 'Governance Operations',
    description: 'Role-Based Access Control with 5-tier hierarchy, HIPAA/SOX/FDA compliant',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/rbac'),
      'RoleManagementDashboard',
    ),
  },
  {
    id: 'actor-descriptor-manager',
    label: 'Actor Descriptor Manager',
    category: 'Governance Operations',
    description: 'Actor lifecycle, message routing, supervision tree, capability tracking',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/actors/components'),
      'ActorDescriptorManager',
    ),
  },
  {
    id: 'actor-mailbox',
    label: 'Actor Mailbox Viewer',
    category: 'Governance Operations',
    description: 'Message queue monitoring, mailbox status, message flow tracking',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/actors/components'),
      'ActorMailboxViewer',
    ),
    props: { actorId: 'demo-actor-id' },
  },
  {
    id: 'tax-notifications',
    label: 'Tax Notification System',
    category: 'Governance Operations',
    description: 'Tax table change notifications with 4-stage timeline and emergency alerts',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/taxNotifications'),
      'TaxNotificationDashboard',
    ),
  },
  {
    id: 'integration-health',
    label: 'Integration Health Monitor',
    category: 'Governance Operations',
    description: 'External system status, API health, connectivity monitoring',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/governance'),
      'IntegrationHealthMonitor',
    ),
  },

  // ── Configuration ───────────────────────────────────────────────
  {
    id: 'dynamic-configuration',
    label: 'Dynamic Configuration',
    category: 'Configuration',
    description: 'Zero hardcoded values demonstration with live backend configuration',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced/DynamicConfigurationShowcase'),
      'DynamicConfigurationShowcase',
    ),
    props: { tenant: 'demo-tenant' },
  },
  {
    id: 'domain-config',
    label: 'Domain Configuration Manager',
    category: 'Configuration',
    description: 'Multi-tenant settings, domain-specific rules, tenant customization',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced'),
      'DomainConfigurationManager',
    ),
    props: { tenant: 'demo-tenant' },
  },
  {
    id: 'communication-manager',
    label: 'Communication Manager',
    category: 'Configuration',
    description: 'Notification settings, email templates, communication channels',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced'),
      'CommunicationManager',
    ),
    props: { tenant: 'demo-tenant' },
  },
  {
    id: 'complex-process',
    label: 'Complex Process Manager',
    category: 'Configuration',
    description: 'Advanced workflow patterns, decision trees, conditional routing',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced'),
      'ComplexProcessManager',
    ),
  },
  {
    id: 'standards-recommendations',
    label: 'Standards Pack Recommendations',
    category: 'Configuration',
    description: 'Auto-recommendations for standards packs based on workflow analysis',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/compliance'),
      'StandardsPackRecommendations',
    ),
    props: { recommendations: [], onApply: async () => {} },
  },

  // ── Dev Tools & Demos ───────────────────────────────────────────
  {
    id: 'legacy-browser',
    label: 'Legacy Component Browser',
    category: 'Dev Tools & Demos',
    description: 'Browse and integrate legacy workflow components with production readiness assessment',
    component: lazyNamed(
      () => import('@ferp/workflow-forms'),
      'LegacyComponentBrowser',
    ),
  },
  {
    id: 'metadata-dsl-demo',
    label: 'Metadata DSL Demo',
    category: 'Dev Tools & Demos',
    description: 'Live metadata DSL system - business-friendly forms generating 25 primitive types',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/metadata/demo/MetadataInterpreterDemo'),
      'MetadataInterpreterDemo',
    ),
  },
  {
    id: 'schema-import-demo',
    label: 'Schema Import (AI-Assisted)',
    category: 'Dev Tools & Demos',
    description: 'LLM-assisted schema import from Medplum FHIR, ISO 20022 - auto-generates metadata',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/schema/SchemaImportDemo'),
      'SchemaImportDemo',
    ),
  },
  {
    id: 'hierarchical-metadata',
    label: 'Hierarchical Metadata Example',
    category: 'Dev Tools & Demos',
    description: 'Interactive demo of 4-layer metadata system (Domain -> Tenant -> Workflow -> Runtime)',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/examples/HierarchicalMetadataExample'),
      'HierarchicalMetadataExample',
    ),
  },
  {
    id: 'comprehensive-task-form',
    label: 'Task Form (185+ Fields)',
    category: 'Dev Tools & Demos',
    description: '185+ field support with nested hierarchy, dual control, 7 compliance frameworks',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/enhanced'),
      'ComprehensiveTaskFormDemo',
    ),
  },
  {
    id: 'healthcare-rcm-demo',
    label: 'Healthcare RCM Demo',
    category: 'Dev Tools & Demos',
    description: '8-step Healthcare RCM workflow: Patient Registration through X12 Claims to Payroll',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/examples/HealthcareRCMDemo'),
      'HealthcareRCMDemo',
    ),
  },
  {
    id: 'sprint7-dashboard',
    label: 'Sprint 7 Dashboard',
    category: 'Dev Tools & Demos',
    description: 'Unified Sprint 7: SAGA monitoring, safety events, system health, production metrics',
    component: lazyNamed(
      () => import('@ferp/workflow-forms/sprint7/Sprint7Dashboard'),
      'Sprint7Dashboard',
    ),
  },
];

// ============================================================================
// Category metadata for sidebar icons
// ============================================================================

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'Workflow Design': <IconBrandStackshare size={16} />,
  'Chaos Ingestion': <IconUpload size={16} />,
  'Safety & Compliance': <IconShield size={16} />,
  'SAGA & Transactions': <IconNetwork size={16} />,
  'Governance & Policy': <IconGavel size={16} />,
  'Analytics & Monitoring': <IconChartBar size={16} />,
  'Runtime Operations': <IconSettings size={16} />,
  'Expert Routing': <IconActivity size={16} />,
  'Audit & Forensics': <IconEye size={16} />,
  'Primitive Dashboards': <IconDatabase size={16} />,
  'Governance Operations': <IconKey size={16} />,
  'Configuration': <IconTool size={16} />,
  'Dev Tools & Demos': <IconRobot size={16} />,
};

// ============================================================================
// Error boundary for individual component rendering
// ============================================================================

class ComponentErrorBoundary extends React.Component<
  { children: React.ReactNode; componentId: string },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Component failed to load">
          <Text size="sm">
            <strong>{this.props.componentId}</strong> threw an error: {this.state.error.message}
          </Text>
          <Text size="xs" c="dimmed" mt="xs">
            This component may need props or context not available in explorer mode.
          </Text>
        </Alert>
      );
    }
    return this.props.children;
  }
}

// ============================================================================
// Feature Catalog View — browse routable pages with layer assignment toggles
// ============================================================================

const LAYER_COLORS: Record<ViewLayer, string> = {
  User: 'blue',
  Admin: 'orange',
  Architect: 'violet',
};

function FeatureCard({
  feature,
  onToggleLayer,
}: {
  feature: CatalogFeature;
  onToggleLayer: (featureId: string, layer: ViewLayer, enabled: boolean) => void;
}) {
  return (
    <Card withBorder shadow="sm" p="md" radius="md">
      <Group justify="space-between" mb="xs" wrap="nowrap">
        <Group gap="xs" wrap="nowrap" style={{ overflow: 'hidden' }}>
          <ThemeIcon variant="light" size="lg" color="gray">
            {React.createElement(getIcon(feature.icon), { size: 20 })}
          </ThemeIcon>
          <div style={{ overflow: 'hidden' }}>
            <Text fw={600} size="sm" truncate>
              {feature.title}
            </Text>
            <Text size="xs" c="dimmed" truncate>
              {feature.componentName}
            </Text>
          </div>
        </Group>
        <Badge
          variant="filled"
          color={LAYER_COLORS[feature.defaultLayer]}
          size="xs"
          style={{ flexShrink: 0 }}
        >
          {feature.defaultLayer}
        </Badge>
      </Group>

      <Group gap={4} mb="xs">
        <Badge size="xs" variant="light" color="gray">
          {feature.category}
        </Badge>
        {feature.requiresLifeCriticalAuth && (
          <Badge size="xs" variant="light" color="red">
            Life-Critical
          </Badge>
        )}
      </Group>

      <Divider mb="sm" />

      <Stack gap="xs">
        <Switch
          label="Show in User View"
          size="xs"
          checked={feature.visibleIn.some((l) => l === 'User')}
          onChange={(e) => onToggleLayer(feature.id, 'User', e.currentTarget.checked)}
          disabled={feature.defaultLayer === 'User'}
        />
        <Switch
          label="Show in Admin View"
          size="xs"
          checked={feature.visibleIn.some((l) => l === 'Admin')}
          onChange={(e) => onToggleLayer(feature.id, 'Admin', e.currentTarget.checked)}
          disabled={feature.defaultLayer === 'Admin'}
        />
      </Stack>
    </Card>
  );
}

function FeatureCatalogView() {
  const {
    features,
    categories,
    toggleFeatureLayer,
    resetOverrides,
    overrideCount,
    loading,
  } = useFeatureCatalog();
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const filtered = useMemo(() => {
    return features.filter((f) => {
      const matchesSearch =
        !search ||
        f.title.toLowerCase().includes(search.toLowerCase()) ||
        f.componentName.toLowerCase().includes(search.toLowerCase()) ||
        f.category.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = filterCategory === 'all' || f.category === filterCategory;
      return matchesSearch && matchesCategory;
    });
  }, [features, search, filterCategory]);

  if (loading) {
    return (
      <Center h={400}>
        <Loader type="dots" />
      </Center>
    );
  }

  return (
    <Stack gap="md" p="md">
      <Group justify="space-between">
        <div>
          <Title order={4}>Feature Catalog</Title>
          <Text size="sm" c="dimmed">
            Browse all {features.length} routable features. Toggle switches to promote into Admin or User views.
          </Text>
        </div>
        <Group gap="xs">
          {overrideCount > 0 && (
            <Badge variant="light" color="orange">
              {overrideCount} override{overrideCount !== 1 ? 's' : ''}
            </Badge>
          )}
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconRotate size={14} />}
            onClick={resetOverrides}
            disabled={overrideCount === 0}
          >
            Reset to Defaults
          </Button>
        </Group>
      </Group>

      <Group gap="sm">
        <TextInput
          placeholder="Search features..."
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          size="xs"
          style={{ flex: 1, maxWidth: 300 }}
        />
        <SegmentedControl
          size="xs"
          value={filterCategory}
          onChange={setFilterCategory}
          data={[
            { label: 'All', value: 'all' },
            ...categories.map((c) => ({ label: c, value: c })),
          ]}
          style={{ overflowX: 'auto' }}
        />
        <Badge variant="light" size="sm">
          {filtered.length} feature{filtered.length !== 1 ? 's' : ''}
        </Badge>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
        {filtered.map((feature) => (
          <FeatureCard
            key={feature.id}
            feature={feature}
            onToggleLayer={toggleFeatureLayer}
          />
        ))}
      </SimpleGrid>

      {filtered.length === 0 && (
        <Center h={200}>
          <Stack ta="center" gap="sm">
            <IconLayoutGrid size={48} opacity={0.3} />
            <Text c="dimmed">No features match your search</Text>
          </Stack>
        </Center>
      )}
    </Stack>
  );
}

// ============================================================================
// Component Browser View (existing explorer, extracted into its own component)
// ============================================================================

function ComponentBrowserView() {
  const [selectedId, setSelectedId] = useState<string>('workflow-system-integrator');
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');

  const categories = useMemo(
    () => [...new Set(COMPONENTS.map((c) => c.category))],
    [],
  );

  const filtered = useMemo(() => {
    return COMPONENTS.filter((c) => {
      const matchesSearch =
        !search ||
        c.label.toLowerCase().includes(search.toLowerCase()) ||
        c.description.toLowerCase().includes(search.toLowerCase());
      const matchesCategory = filterCategory === 'all' || c.category === filterCategory;
      return matchesSearch && matchesCategory;
    });
  }, [search, filterCategory]);

  const grouped = useMemo(() => {
    const map = new Map<string, ExplorerComponent[]>();
    for (const c of filtered) {
      const list = map.get(c.category) ?? [];
      list.push(c);
      map.set(c.category, list);
    }
    return map;
  }, [filtered]);

  const selected = COMPONENTS.find((c) => c.id === selectedId);

  return (
    <Box style={{ display: 'flex', height: 'calc(100vh - 180px)' }}>
      {/* Sidebar - component list */}
      <Box
        style={{
          width: 320,
          minWidth: 320,
          borderRight: '1px solid var(--mantine-color-default-border)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack gap="xs" p="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
          <Group justify="space-between">
            <Title order={5}>Components</Title>
            <Badge variant="light" size="sm">{filtered.length}</Badge>
          </Group>
          <TextInput
            placeholder="Search components..."
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            size="xs"
          />
          <SegmentedControl
            size="xs"
            value={filterCategory}
            onChange={setFilterCategory}
            data={[
              { label: 'All', value: 'all' },
              ...categories.map((c) => ({ label: c.split(' ')[0], value: c })),
            ]}
            style={{ overflowX: 'auto' }}
            fullWidth={false}
          />
        </Stack>

        <ScrollArea style={{ flex: 1 }} p="xs">
          <Stack gap={2}>
            {[...grouped.entries()].map(([category, items]) => (
              <NavLink
                key={category}
                label={category}
                leftSection={CATEGORY_ICONS[category] ?? <IconComponents size={16} />}
                childrenOffset={16}
                defaultOpened={items.some((i) => i.id === selectedId)}
              >
                {items.map((item) => (
                  <NavLink
                    key={item.id}
                    label={item.label}
                    description={item.description.slice(0, 60) + (item.description.length > 60 ? '...' : '')}
                    active={selectedId === item.id}
                    onClick={() => setSelectedId(item.id)}
                    variant="filled"
                    styles={{ label: { fontSize: 'var(--mantine-font-size-xs)' }, description: { fontSize: '10px' } }}
                  />
                ))}
              </NavLink>
            ))}
          </Stack>
        </ScrollArea>
      </Box>

      {/* Main content - selected component */}
      <Box style={{ flex: 1, overflow: 'auto' }} p="md">
        {selected ? (
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Title order={3}>{selected.label}</Title>
                <Text size="sm" c="dimmed">{selected.description}</Text>
              </div>
              <Badge variant="light">{selected.category}</Badge>
            </Group>

            <Card withBorder p={0} style={{ minHeight: 400 }}>
              <ComponentErrorBoundary componentId={selected.id}>
                <Suspense
                  fallback={
                    <Center h={400}>
                      <Stack ta="center" gap="sm">
                        <Loader type="dots" />
                        <Text size="sm" c="dimmed">Loading {selected.label}...</Text>
                      </Stack>
                    </Center>
                  }
                >
                  <Box p="md">
                    {React.createElement(
                      selected.component as React.ComponentType<Record<string, unknown>>,
                      selected.props ?? {},
                    )}
                  </Box>
                </Suspense>
              </ComponentErrorBoundary>
            </Card>
          </Stack>
        ) : (
          <Center h={400}>
            <Stack ta="center" gap="sm">
              <IconComponents size={48} opacity={0.3} />
              <Text c="dimmed">Select a component from the sidebar</Text>
            </Stack>
          </Center>
        )}
      </Box>
    </Box>
  );
}

// ============================================================================
// Main Component Explorer Page — Features (default) | Components
// ============================================================================

export function ComponentExplorerPage() {
  const [view, setView] = useState<string>('features');

  return (
    <Stack gap="sm">
      <Group justify="space-between" p="md" pb={0}>
        <div>
          <Title order={3}>Explorer</Title>
          <Text size="sm" c="dimmed">
            Browse features and components — promote to Admin or User views
          </Text>
        </div>
        <SegmentedControl
          value={view}
          onChange={setView}
          data={[
            { label: 'Features', value: 'features' },
            { label: 'Components', value: 'components' },
          ]}
          size="sm"
        />
      </Group>

      {view === 'features' ? <FeatureCatalogView /> : <ComponentBrowserView />}
    </Stack>
  );
}
